import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogout } from '../utils/adminSession';
import { reportClientError } from '../utils/clientLogger';
import {
  ADMIN_VENDEDOR_FORM_INITIAL_STATE,
  createVendedor,
  deleteLead,
  deleteVendedor,
  fetchAdminVendedoresData,
  fetchVendedorLeads,
  toggleVendedorStatus,
  updateVendedor,
} from '../utils/adminVendedoresApi';
import { AdminVendedoresHeader } from '../components/admin/vendedores/AdminVendedoresHeader';
import { AdminVendedoresMetrics } from '../components/admin/vendedores/AdminVendedoresMetrics';
import { AdminVendedoresTable } from '../components/admin/vendedores/AdminVendedoresTable';
import { AdminVendedorFormModal } from '../components/admin/vendedores/AdminVendedorFormModal';
import { AdminVendedorLeadsModal } from '../components/admin/vendedores/AdminVendedorLeadsModal';

const SITE_URL = window.location.origin;

const AdminVendedores = () => {
  const navigate = useNavigate();
  const [vendedores, setVendedores] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [leads, setLeads] = useState([]);
  const [formData, setFormData] = useState(ADMIN_VENDEDOR_FORM_INITIAL_STATE);
  const [copiedLink, setCopiedLink] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const data = await fetchAdminVendedoresData();
      setVendedores(data.vendedores);
      setMetrics(data.metrics);
    } catch (err) {
      reportClientError('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (e) {
      reportClientError('Logout error:', e);
    } finally {
      navigate('/admin/login');
    }
  };

  const handleAddVendedor = async (e) => {
    e.preventDefault();
    try {
      await createVendedor(formData);
      setShowAddModal(false);
      setFormData(ADMIN_VENDEDOR_FORM_INITIAL_STATE);
      await loadData();
    } catch (err) {
      alert('Erro ao adicionar vendedor');
    }
  };

  const handleEditVendedor = async (e) => {
    e.preventDefault();
    try {
      await updateVendedor(selectedVendedor.id, formData);
      setShowEditModal(false);
      setSelectedVendedor(null);
      await loadData();
    } catch (err) {
      alert('Erro ao atualizar vendedor');
    }
  };

  const handleToggleAtivo = async (vendedor) => {
    try {
      await toggleVendedorStatus(vendedor);
      await loadData();
    } catch (err) {
      alert('Erro ao atualizar status');
    }
  };

  const handleDeleteVendedor = async (vendedor) => {
    if (!window.confirm(`Tem certeza que deseja remover ${vendedor.nome}?`)) return;
    try {
      await deleteVendedor(vendedor.id);
      await loadData();
    } catch (err) {
      alert('Erro ao remover vendedor');
    }
  };

  const handleViewLeads = async (vendedor) => {
    try {
      const response = await fetchVendedorLeads(vendedor.id);
      setLeads(response);
      setSelectedVendedor(vendedor);
      setShowLeadsModal(true);
    } catch (err) {
      alert('Erro ao carregar leads');
    }
  };

  const handleDeleteLead = async (lead) => {
    if (!window.confirm(`Excluir o lead de ${lead.cliente_nome}?`)) return;

    try {
      await deleteLead(lead.id);
      if (selectedVendedor) {
        await handleViewLeads(selectedVendedor);
      }
      await loadData();
    } catch (err) {
      alert('Erro ao excluir lead');
    }
  };

  const copyLink = (vendedor) => {
    const link = `${SITE_URL}/v/${vendedor.codigo}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(vendedor.id);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const openEdit = (vendedor) => {
    setSelectedVendedor(vendedor);
    setFormData({
      nome: vendedor.nome,
      telefone: vendedor.telefone || '',
      whatsapp: vendedor.whatsapp || '',
      email: vendedor.email || '',
      foto_url: vendedor.foto_url || '',
      especialidade: vendedor.especialidade || 'todos'
    });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="admin-vendedores-loading-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" data-testid="admin-vendedores-loading-spinner"></div>
      </div>
    );
  }

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setFormData(ADMIN_VENDEDOR_FORM_INITIAL_STATE);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedVendedor(null);
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-vendedores-page">
      <AdminVendedoresHeader onOpenAddModal={() => setShowAddModal(true)} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <AdminVendedoresMetrics metrics={metrics} />
        <AdminVendedoresTable
          vendedores={vendedores}
          copiedLink={copiedLink}
          onCopyLink={copyLink}
          onViewLeads={handleViewLeads}
          onToggleAtivo={handleToggleAtivo}
          onOpenEdit={openEdit}
          onDelete={handleDeleteVendedor}
        />
      </div>

      <AdminVendedorFormModal
        isOpen={showAddModal}
        mode="add"
        formData={formData}
        setFormData={setFormData}
        onClose={handleCloseAddModal}
        onSubmit={handleAddVendedor}
      />

      <AdminVendedorFormModal
        isOpen={showEditModal}
        mode="edit"
        formData={formData}
        setFormData={setFormData}
        onClose={handleCloseEditModal}
        onSubmit={handleEditVendedor}
      />

      <AdminVendedorLeadsModal
        isOpen={showLeadsModal}
        selectedVendedor={selectedVendedor}
        leads={leads}
        onClose={() => {
          setShowLeadsModal(false);
          setSelectedVendedor(null);
        }}
        onDeleteLead={handleDeleteLead}
      />
    </div>
  );
};

export default AdminVendedores;
